/**
 * Application entry point that sets up providers and global configuration.
 *
 * What: Boots the React app, preloads route-specific marketing data, and
 * chooses the correct client mount strategy for each route.
 * Why: Public marketing pages ship browser-captured prerender HTML for SEO,
 * which is not safe to `hydrateRoot()` like true React SSR output.
 * When: Update when modifying global providers, prerender bootstrap behavior,
 * authentication config, or app initialization.
 */
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { CssBaseline, type Theme, ThemeProvider } from '@mui/material';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

import {
  isPublicMarketingRoutePath,
  normalizePublicMarketingRoutePath,
} from '@shared/seo/publicMarketingRoutes.js';

import { Auth0ProviderWithNavigate } from './auth/Auth0ProviderWithNavigate';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import {
  getBehaviorConfig,
  getBrandingConfig,
  getIdentityConfig,
  loadBrandConfig,
} from './config/runtimeBrandConfig';
import { buildBrandedTheme } from './config/runtimeBrandedTheme';
import { SoundProvider } from './contexts/SoundContext';
import { AuthProvider } from './hooks/useAuth';
import { preloadMarketingRouteModule } from './prerender/marketingRouteModules';
import { SessionExpiredError, setApiBaseUrl } from './services/apiUtils';
import { trackAppStart } from './services/appMetrics';
import { captureException, initializeSentry } from './services/sentryService';
import { hasAuth0SessionInStorage } from './utils/auth0SessionStorage';
import { installDefaultSeoFallbackCleanup } from './utils/defaultSeoFallback';
import { deferNonCritical } from './utils/deferNonCritical';
import { setupGlobalErrorHandlers } from './utils/globalErrorHandlers';
import logger from './utils/logger';
import { initializeMetaPixel } from './utils/metaPixel.js';
import { initializePlausible } from './utils/plausible';
import App from './App';

import './styles/fonts.css';
import './styles/safe-areas.css';
import './styles/landing-marquee.css';
import './config/designTokens.css';

// Handled data-layer failures used to die in per-caller console.error calls
// and never reach Sentry as issues. Report them centrally, minus the classes
// that are expected flows or already tracked elsewhere:
// - auth/not-found/rate-limit statuses and session expiry drive UI flows;
// - network flakes (offline mobile) are environmental, not actionable;
// - 5xx are captured server-side with the real stack, so re-reporting the
//   browser's view of them would only double-count.
const IGNORED_DATA_ERROR_STATUSES = new Set([401, 403, 404, 429]);

const isNetworkFlakeError = (error: Error): boolean =>
  error.name === 'AbortError' ||
  (error instanceof TypeError &&
    /fetch|network|load failed|connection/i.test(error.message));

const shouldReportDataLayerError = (error: unknown): boolean => {
  if (error instanceof SessionExpiredError) return false;
  if (error instanceof Error) {
    if (isNetworkFlakeError(error)) return false;
    const status = (error as { status?: unknown }).status;
    if (typeof status === 'number') {
      if (IGNORED_DATA_ERROR_STATUSES.has(status)) return false;
      if (status >= 500) return false;
    }
    if (/^Server error: 5\d\d$/.test(error.message)) return false;
  }
  return true;
};

const reportDataLayerError = (
  error: unknown,
  extra: Record<string, unknown>
): void => {
  if (!shouldReportDataLayerError(error)) return;
  captureException(error instanceof Error ? error : new Error(String(error)), {
    tags: { errorType: 'data_layer' },
    extra,
  });
};

// Single process-wide QueryClient. Adaptive-practice hooks (added in PR 2)
// read from this cache; wiring the provider here sets up the infra without
// any caller in PR 1.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) =>
      reportDataLayerError(error, {
        source: 'query',
        queryKey: JSON.stringify(query.queryKey),
      }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      reportDataLayerError(error, {
        source: 'mutation',
        mutationKey: JSON.stringify(mutation.options.mutationKey ?? null),
      }),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

type MaybeBrandingConfig = ReturnType<typeof getBrandingConfig>;

type RenderAppTreeArgs = {
  appTheme: Theme;
  auth0ClientId: string;
  auth0Domain: string;
  redirectPath: string;
};

const APP_BOOTSTRAP_ERROR_KEY = '__APP_BOOTSTRAP_ERROR__';

type WindowWithBootstrapError = Window & {
  [APP_BOOTSTRAP_ERROR_KEY]?: string;
};

const applyBrandingToDocument = (branding: MaybeBrandingConfig) => {
  if (!branding) return;
  if (branding.siteTitle) {
    document.title = branding.siteTitle;
  }
  if (branding.faviconUrl) {
    const setIcon = (rel: string, type?: string) => {
      let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        if (type) {
          link.setAttribute('type', type);
        }
        document.head.appendChild(link);
      }
      link.setAttribute('href', branding.faviconUrl!);
    };
    setIcon('icon', 'image/x-icon');
    setIcon('shortcut icon', 'image/x-icon');
  }
};

const valueOrEnv = (
  value: string | null | undefined,
  envFallback: string | undefined
) => {
  const isPlaceholder =
    typeof value === 'string' && /^\$\{[A-Z0-9_]+\}$/.test(value);
  return isPlaceholder ? envFallback : value || envFallback;
};

const clearBootstrapError = () => {
  const windowWithBootstrapError = window as WindowWithBootstrapError;
  delete windowWithBootstrapError[APP_BOOTSTRAP_ERROR_KEY];
};

const setBootstrapError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  const windowWithBootstrapError = window as WindowWithBootstrapError;
  windowWithBootstrapError[APP_BOOTSTRAP_ERROR_KEY] = message;
  console.error('App bootstrap failed:', error);
};

const renderAppTree = ({
  appTheme,
  auth0ClientId,
  auth0Domain,
  redirectPath,
}: RenderAppTreeArgs) => {
  // Data router rather than <BrowserRouter>: `useBlocker` is data-router-only,
  // and it is the single guard that intercepts every in-app navigation away
  // from a running practice session (links, programmatic navigate, browser
  // back). Built here — `renderAppTree` runs once per boot, so StrictMode's
  // double render can't mint a second router.
  //
  // One catch-all route: <App /> keeps owning the route table through its own
  // descendant <Routes>.
  const router = createBrowserRouter([
    {
      path: '*',
      // Router wraps auth: Auth0ProviderWithNavigate completes the login
      // redirect via useNavigate (appState.returnTo), which resolves inside
      // a route element.
      //
      // The boundary has to live INSIDE the element. A data router always
      // wraps its root match in an error boundary of its own (react-router
      // `_renderMatches`, `index === 0`), which would otherwise catch first
      // and render its generic "Unexpected Application Error!" page —
      // skipping Sentry capture, chunk-load recovery and the branded retry.
      // App's own `app_routes` boundary covers the route table; this one
      // covers the auth providers and the shell around it (Header, nav).
      element: (
        <ErrorBoundary sourceLocation="app_shell" resetOnRetry={true}>
          <Auth0ProviderWithNavigate
            domain={auth0Domain}
            clientId={auth0ClientId}
            // Refresh tokens persisted in localStorage so silent renewal works on
            // Safari Mobile, where ITP blocks the iframe-based silent-auth fallback.
            useRefreshTokens={true}
            cacheLocation="localstorage"
            authorizationParams={{
              redirect_uri: `${window.location.origin}${redirectPath}`,
              // `offline_access` requests a refresh token (required by useRefreshTokens).
              scope: 'openid profile email offline_access',
              // No `audience` and no `response_type`: Auth0 free tier doesn't
              // support custom API audiences, so we authenticate with ID tokens
              // (validated server-side against client ID). The SDK defaults to
              // Authorization Code Flow with PKCE when response_type is omitted.
            }}
          >
            <AuthProvider>
              <App />
            </AuthProvider>
          </Auth0ProviderWithNavigate>
        </ErrorBoundary>
      ),
    },
  ]);

  return (
    <React.StrictMode>
      <ErrorBoundary sourceLocation="app_providers" resetOnRetry={true}>
        <HelmetProvider>
          <ThemeProvider theme={appTheme}>
            <CssBaseline />
            <QueryClientProvider client={queryClient}>
              <SoundProvider>
                <RouterProvider router={router} />
              </SoundProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
};

const preloadHydrationDataForPath = async (routePath: string) => {
  if (!routePath.startsWith('/blog/')) {
    return;
  }

  const [{ getBlogPostContentFilePath }, { preloadRawBlogPostContent }] =
    await Promise.all([
      import('@/content/blog/blogPostRepository'),
      import('@/content/blog/blogPostContentLoader'),
    ]);

  const contentFilePath = getBlogPostContentFilePath(routePath);
  if (!contentFilePath) {
    return;
  }

  await preloadRawBlogPostContent(contentFilePath);
};

const mountApp = async (appTree: React.ReactElement) => {
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    throw new Error('Missing root element for app bootstrap.');
  }

  const routePath = window.location.pathname;
  const normalizedRoutePath = normalizePublicMarketingRoutePath(routePath);
  const hasServerMarkup = rootElement.innerHTML.trim().length > 0;
  const shouldClientRenderPrerenderedMarketingRoute =
    hasServerMarkup && isPublicMarketingRoutePath(normalizedRoutePath);

  if (!hasServerMarkup) {
    createRoot(rootElement).render(appTree);
    return;
  }

  await Promise.all([
    preloadMarketingRouteModule(routePath),
    preloadHydrationDataForPath(routePath),
  ]);

  if (shouldClientRenderPrerenderedMarketingRoute) {
    // Marketing pages are browser-DOM snapshots, so use a clean client mount.
    // Keep the snapshot visible until React replaces it during commit:
    // root.render() schedules work, so clearing here would paint a blank page.
    createRoot(rootElement).render(appTree);
    return;
  }

  hydrateRoot(rootElement, appTree);
};

const init = async () => {
  clearBootstrapError();

  // Watch for Helmet to commit the page-specific meta description, then drop
  // the index.html `[data-default-seo]` fallbacks so JS-enabled crawlers
  // never see duplicate description / og / theme-color tags. Registered
  // before React mounts so the observer is in place when Helmet commits.
  installDefaultSeoFallbackCleanup();

  // Load brand config (fallback to env defaults on failure)
  try {
    await loadBrandConfig();
  } catch (err) {
    // Non-fatal for rendering, but this config also decides the API base URL
    // below. A console-only warning is how a boot-time network blip silently
    // becomes "every API call fails" on one device, so report it.
    console.warn('Failed to load brand-config.json, using env defaults', err);
    captureException(err instanceof Error ? err : new Error(String(err)), {
      level: 'warning',
      tags: { area: 'boot' },
    });
  }

  const identity = getIdentityConfig();
  const branding = getBrandingConfig();
  const behavior = getBehaviorConfig();

  // Configure API base URL from behavior
  const resolvedApiBaseUrl = valueOrEnv(
    behavior?.apiBaseUrl as string | undefined,
    import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
  );
  if (resolvedApiBaseUrl) {
    setApiBaseUrl(resolvedApiBaseUrl);
  }

  // Apply document title and favicon
  applyBrandingToDocument(branding);

  // Set up global error handlers early (unhandled promise rejections, global errors).
  // These work without Sentry — they just console.log until Sentry is ready.
  try {
    setupGlobalErrorHandlers();
  } catch (err) {
    // Non-fatal: log error but continue app initialization
    console.warn('Failed to set up global error handlers:', err);
  }

  // Load Meta Pixel on first interaction (or shortly after window load) so
  // the ~100 KB fbevents.js fetch stays out of the initial-load window. Ad
  // attribution is unaffected: the _fbp/_fbc cookies exist long before any
  // lead form submit, and interaction always precedes conversion events.
  const metaPixelId = import.meta.env.VITE_META_PIXEL_ID;
  if (metaPixelId) {
    deferNonCritical(() => {
      initializeMetaPixel(metaPixelId).catch(err => {
        // Non-fatal: log error but continue app initialization
        console.warn('Failed to initialize Meta Pixel:', err);
      });
    });
  }

  const plausibleDomain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
  if (plausibleDomain) {
    deferNonCritical(() => {
      // Auth0 SPA SDK with cacheLocation=localstorage writes keys prefixed with
      // `@@auth0spajs@@::` once a session exists. Treat the presence of any
      // such key as "logged-in user, skip tracking" so we don't pollute
      // visitor analytics with our own admin/student traffic.
      const isAuthenticated = hasAuth0SessionInStorage();
      initializePlausible({
        domain: plausibleDomain,
        isAuthenticated,
      }).catch(err => {
        console.warn('Failed to initialize Plausible:', err);
      });
    });
  }

  // Build themed palette from branding overrides
  const appTheme = buildBrandedTheme(branding);

  const auth0Domain =
    valueOrEnv(identity?.auth0?.domain, import.meta.env.VITE_AUTH0_DOMAIN) ||
    '';
  const auth0ClientId =
    valueOrEnv(
      identity?.auth0?.clientId,
      import.meta.env.VITE_AUTH0_CLIENT_ID
    ) || '';
  const redirectPath = identity?.auth0?.redirectUris?.[0] || '/callback';

  const appTree = renderAppTree({
    appTheme,
    auth0ClientId,
    auth0Domain,
    redirectPath,
  });

  await mountApp(appTree);

  // Initialize Sentry and metrics on first interaction (or shortly after
  // window load) so the SDK's ~500 KB never competes with the critical
  // rendering path. Errors thrown before then are still console-logged by
  // the global handlers registered above; trackAppStart reads absolute
  // navigation timing, so the later call doesn't skew the startup metric.
  deferNonCritical(() => {
    initializeSentry()
      .then(() => {
        trackAppStart();
        logger.info('Logger initialized');
      })
      .catch(err => {
        console.warn('Failed to initialize Sentry:', err);
      });
  });
};

void init().catch(error => {
  setBootstrapError(error);
});
